import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import Landing from "@/pages/Landing";
import About from "@/pages/About";
import Conditions from "@/pages/Conditions";
import Contact from "@/pages/Contact";
import Book from "@/pages/Book";
import Blog from "@/pages/Blog";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import DashboardLayout from "@/components/DashboardLayout";
import Dashboard from "@/pages/Dashboard";
import Patients from "@/pages/Patients";
import PatientDetail from "@/pages/PatientDetail";
import Appointments from "@/pages/Appointments";
import Prescriptions from "@/pages/Prescriptions";
import Billing from "@/pages/Billing";
import Leads from "@/pages/Leads";
import AiAssist from "@/pages/AiAssist";
import Staff from "@/pages/Staff";
import ForceChangePassword from "@/pages/ForceChangePassword";
import PatientPortal from "@/pages/PatientPortal";
import "@/App.css";

function Protected({ children, roles }) {
  const { user, loading } = useAuth();
  const loc = useLocation();
  if (loading) return <div className="h-screen grid place-items-center text-[color:var(--muted)]">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (user.must_change_password && loc.pathname !== "/force-change-password") {
    return <Navigate to="/force-change-password" replace />;
  }
  if (roles && !roles.includes(user.role)) return <Navigate to="/" replace />;
  return children;
}

function RoleHome() {
  const { user } = useAuth();
  if (!user) return <Navigate to="/login" replace />;
  if (user.role === "patient") return <Navigate to="/portal" replace />;
  return <Navigate to="/dashboard" replace />;
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Routes>
          {/* Public marketing */}
          <Route path="/" element={<Landing />} />
          <Route path="/about" element={<About />} />
          <Route path="/conditions" element={<Conditions />} />
          <Route path="/blog" element={<Blog />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/book" element={<Book />} />

          {/* Auth */}
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/force-change-password" element={<Protected><ForceChangePassword /></Protected>} />

          {/* Staff dashboard */}
          <Route path="/dashboard" element={<Protected roles={["admin","doctor","receptionist"]}><DashboardLayout><Dashboard /></DashboardLayout></Protected>} />
          <Route path="/patients" element={<Protected roles={["admin","doctor","receptionist"]}><DashboardLayout><Patients /></DashboardLayout></Protected>} />
          <Route path="/patients/:id" element={<Protected roles={["admin","doctor","receptionist"]}><DashboardLayout><PatientDetail /></DashboardLayout></Protected>} />
          <Route path="/appointments" element={<Protected roles={["admin","doctor","receptionist"]}><DashboardLayout><Appointments /></DashboardLayout></Protected>} />
          <Route path="/prescriptions" element={<Protected roles={["admin","doctor"]}><DashboardLayout><Prescriptions /></DashboardLayout></Protected>} />
          <Route path="/billing" element={<Protected roles={["admin","doctor","receptionist"]}><DashboardLayout><Billing /></DashboardLayout></Protected>} />
          <Route path="/leads" element={<Protected roles={["admin","doctor","receptionist"]}><DashboardLayout><Leads /></DashboardLayout></Protected>} />
          <Route path="/ai" element={<Protected roles={["admin","doctor"]}><DashboardLayout><AiAssist /></DashboardLayout></Protected>} />
          <Route path="/staff" element={<Protected roles={["admin"]}><DashboardLayout><Staff /></DashboardLayout></Protected>} />

          {/* Patient portal */}
          <Route path="/portal" element={<Protected roles={["patient"]}><PatientPortal /></Protected>} />

          {/* Role-based home redirect */}
          <Route path="/app" element={<RoleHome />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        <Toaster richColors position="top-right" />
      </AuthProvider>
    </BrowserRouter>
  );
}
